// Listing-Detail-Reiter "Optionale Felder".
//
// Optionale Felder beziehen sich auf EIN Listing. Pro Kaufland-Kategorie gibt es
// unterschiedliche Felder; gleiche Felder (gleiche Kaufland-attribute_id) ueber mehrere
// Kategorien sind in der DB EIN Feld (Dedup) und werden nicht mehrfach gepflegt.
//
// Pro Feld zeigt der Reiter zwei Werte (dreispaltiges Layout: Feld · Default · Override):
//   - Default (geteilt): tenant-weiter Default fuer dieses Feld — einmal gesetzt, gilt er
//     fuer alle Listings/Kategorien, die dasselbe Feld nutzen.
//   - Override (Artikel): artikelspezifischer Wert, sticht den Default. Leer = erbt Default.
//
// Eingaben sind datentyp-gerecht (Bool → Ja/Nein-Select, Int/Float/Si_* → Zahl mit
// Einheit-Suffix, shared-set → Text mit Auswahl-Badge, sonst Text).
//
// Datenquelle: RPC kaufland_list_optional_field_overrides(_vario_article_id) liefert die
// optionalen Felder der Listing-Kategorie inkl. default_value + override_value (Migration 0123).
// Basis-Felder (base:*) sind kategorie-unabhaengig (Migration 0126/0127).
// Geschrieben via kaufland_set_optional_field_value (Default) bzw.
// kaufland_set_optional_field_override (Override).

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '@vario-connect/frontend-design';

interface OptionalFieldsCtx {
  supabase: SupabaseClient;
  supabaseUrl: string;
  session: { token: string };
}

interface OptionalFieldsListing {
  storefront: string;
  vario_article_id: string;
  kaufland_category_id: number | null;
}

const STOREFRONT_LOCALES: Record<string, string> = {
  de: 'de-DE', at: 'de-AT', cz: 'cs-CZ', sk: 'sk-SK', pl: 'pl-PL', fr: 'fr-FR', it: 'it-IT',
};
function localeFor(storefront: string): string {
  return STOREFRONT_LOCALES[storefront] ?? 'de-DE';
}

interface OverrideRow {
  attribute_id: string;
  name: string | null;
  type: string | null;
  is_shared_set: boolean;
  shared_set_id: number | null;
  unit: string | null;
  description: string | null;
  default_value: unknown;
  override_value: unknown;
}

type InputKind = 'bool' | 'number' | 'text';

/** Eingabe-Widget-Art aus dem Kaufland-Typ. shared-set bleibt 'text' (Autocomplete waere
 *  orthogonal). Lokale Kopie der getesteten Logik aus
 *  packages/marketplace-kaufland/src/optional-fields.ts (Paket ist nicht im Frontend-tsconfig). */
function valueInputKind(kauflandType: string | null | undefined): InputKind {
  if (!kauflandType) return 'text';
  if (kauflandType === 'Bool') return 'bool';
  if (kauflandType === 'Int' || kauflandType === 'Float') return 'number';
  if (kauflandType.startsWith('Si_')) return 'number';
  return 'text';
}

/** Bekannte SI-Einheiten → Kurzzeichen als Eingabe-Suffix. Nur sichere Treffer; unbekannte
 *  Si_-Typen zeigen kein Suffix. r.unit (falls von Kaufland geliefert) hat Vorrang. */
const SI_UNIT_HINT: Record<string, string> = {
  Si_Meter: 'm', Si_Centimeter: 'cm', Si_Millimeter: 'mm', Si_Kilometer: 'km',
  Si_Kilogram: 'kg', Si_Gram: 'g', Si_Milligram: 'mg', Si_Ton: 't',
  Si_Liter: 'l', Si_Milliliter: 'ml',
  Si_Square_Meter: 'm²', Si_Cubic_Meter: 'm³',
  Si_Watt: 'W', Si_Kilowatt: 'kW',
};
function unitHint(r: OverrideRow): string {
  if (r.unit && r.unit.trim().length > 0) return r.unit.trim();
  if (r.type) {
    const hint = SI_UNIT_HINT[r.type];
    if (hint) return hint;
  }
  return '';
}

function isPresent(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string' && v === '') return false;
  return true;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

/** jsonb-Wert -> Anzeige-/Input-String. */
function valueToText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/** Effektiv-Anzeige je Eingabe-Art (bool als Ja/Nein statt true/false). */
function formatEffective(kind: InputKind, v: unknown): string {
  if (kind === 'bool') return v === true ? 'Ja' : v === false ? 'Nein' : '';
  return valueToText(v);
}

const STYLES = `
  .kof-shell { display: flex; flex-direction: column; gap: 14px; padding: 16px; }

  .kof-header { display: flex; align-items: flex-start; gap: 12px; }
  .kof-title { font-size: 16px; font-weight: 600; color: #101828; }
  .kof-subtitle { font-size: 12px; color: #475467; line-height: 1.45; margin-top: 2px; max-width: 64ch; }
  .kof-header-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; margin-left: auto; }
  .kof-count {
    font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; color: #475467;
    white-space: nowrap; background: #F2F4F7; border-radius: 999px; padding: 3px 10px;
  }

  .kof-table {
    border: 1px solid var(--v-border, #E4E7EC); border-radius: 10px; overflow: hidden;
    background: var(--v-surface, #fff);
  }
  .kof-row {
    display: grid;
    grid-template-columns: minmax(220px, 1.3fr) minmax(150px, 1fr) minmax(150px, 1fr);
    gap: 14px; padding: 11px 14px; align-items: start;
    border-top: 1px solid #F2F4F7;
  }
  .kof-row--head {
    border-top: 0; background: #F9FAFB; padding-top: 9px; padding-bottom: 9px;
    font-size: 11px; font-weight: 600; color: #667085; text-transform: uppercase; letter-spacing: 0.03em;
  }
  .kof-row--head > div { align-self: center; }

  .kof-field-label { font-weight: 600; font-size: 14px; color: #101828; display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
  .kof-field-unit { font-size: 11px; color: #98A2B3; font-weight: 500; }
  .kof-field-meta { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #667085; margin-top: 2px; word-break: break-all; }
  .kof-field-eff { font-size: 11px; color: #98A2B3; margin-top: 3px; }
  .kof-field-eff b { color: #475467; font-weight: 600; }
  .kof-badge {
    font-size: 9.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em;
    color: #3538CD; background: #EEF4FF; border-radius: 4px; padding: 1px 5px;
  }

  .kof-cell { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
  .kof-cell input, .kof-cell select {
    width: 100%; height: 34px; padding: 0 10px; box-sizing: border-box;
    border: 1px solid var(--v-border-strong, #D0D5DD); border-radius: 6px;
    background: #fff; font: inherit; font-size: 13px; color: #101828;
  }
  .kof-cell input:focus, .kof-cell select:focus {
    outline: 0; border-color: var(--v-secondary, #1A1D21); box-shadow: 0 0 0 3px rgba(26,29,33,0.15);
  }
  .kof-cell input.is-set, .kof-cell select.is-set { border-color: #F59E0B; }
  .kof-cell-cap { font-size: 10px; color: #98A2B3; text-transform: uppercase; letter-spacing: 0.03em; }
  .kof-cell-cap.is-set { color: #B45309; font-weight: 600; }

  .kof-input-unit { position: relative; display: flex; align-items: center; }
  .kof-input-unit input { padding-right: 34px; }
  .kof-input-unit .kof-unit { position: absolute; right: 10px; font-size: 11px; color: #98A2B3; pointer-events: none; }

  @media (max-width: 760px) {
    .kof-row { grid-template-columns: 1fr; gap: 8px; }
    .kof-row--head { display: none; }
  }
`;

export async function renderOptionalFieldsTab(
  host: HTMLElement,
  ctx: OptionalFieldsCtx,
  listing: OptionalFieldsListing,
): Promise<void> {
  host.innerHTML = `
    <div class="v-pa-md" style="display:flex;align-items:center;gap:0.5em;color:#666;">
      <span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Lade optionale Felder …
    </div>`;

  const { data, error } = await ctx.supabase.rpc('kaufland_list_optional_field_overrides', {
    _vario_article_id: listing.vario_article_id,
  });
  if (error) {
    host.innerHTML = `<div class="v-pa-md v-text-error">Laden fehlgeschlagen: ${escapeHtml(error.message)}</div>`;
    return;
  }
  const rows = (data ?? []) as OverrideRow[];

  if (rows.length === 0) {
    // Kein Listing/Storefront ermittelbar — selbst die kategorie-unabhaengigen Basis-Felder
    // koennen ohne Storefront nicht zugeordnet werden.
    host.innerHTML = `
      <div class="v-pa-md">
        <div class="v-alert v-alert--info">
          Kein Kaufland-Listing zu diesem Artikel gefunden — bitte den Artikel zuerst im Vario-Slot einem Verkaufskanal zuordnen.
        </div>
      </div>`;
    return;
  }

  // base:*-Zeilen sind kategorie-unabhaengige Basis-Felder; alle anderen sind
  // kategorie-spezifische optionale Attribute.
  const categoryRows = rows.filter((r) => !r.attribute_id.startsWith('base:'));

  // Banner: keine Kategorie → nur Basis-Felder; Kategorie gesetzt, aber Attribute (noch)
  // nicht im Cache → Refresh anstossen, Basis-Felder trotzdem rendern.
  let banner = '';
  if (!listing.kaufland_category_id) {
    banner = `<div class="v-alert v-alert--info" style="margin:0;">
      Diesem Listing ist noch keine Kaufland-Kategorie zugewiesen — es werden nur die
      kategorie-unabhängigen Basis-Felder gezeigt. Kategorie im Vario-Slot setzen, um die
      kategorie-spezifischen Felder zu laden.</div>`;
  } else if (categoryRows.length === 0) {
    const { error: enqErr } = await ctx.supabase.rpc('kaufland_enqueue_refresh_category_attributes', {
      _storefront: listing.storefront,
      _category_id: listing.kaufland_category_id,
      _locale: localeFor(listing.storefront),
    });
    banner = `<div class="v-alert v-alert--info" style="margin:0;">
      ${enqErr
        ? `Kategorie-Felder nicht im Cache. Aktualisierung fehlgeschlagen: ${escapeHtml(enqErr.message)}`
        : 'Die kategorie-spezifischen Felder werden gerade von Kaufland geladen. Bitte den Reiter in ein paar Sekunden erneut öffnen (Basis-Felder sind unten bereits pflegbar).'}</div>`;
  }

  const filled = rows.filter((r) => isPresent(r.override_value) || isPresent(r.default_value)).length;

  host.innerHTML = `
    <style>${STYLES}</style>
    <div class="kof-shell">
      <div class="kof-header">
        <div style="flex:1;min-width:0;">
          <div class="kof-title">Optionale Felder</div>
          <div class="kof-subtitle">
            <strong>Default</strong> gilt geteilt für alle Listings, die dasselbe Feld nutzen
            (einmal pflegen). <strong>Override</strong> ist der artikelspezifische Wert (leer = erbt Default).
          </div>
        </div>
        <div class="kof-header-actions">
          <button type="button" id="of-refresh" class="v-button v-button--ghost"
                  title="Optionale Felder dieser Kategorie neu von Kaufland laden">
            <span class="v-icon fa-light fa-rotate"></span> Aktualisieren
          </button>
          <span class="kof-count">${filled} / ${rows.length} belegt</span>
        </div>
      </div>
      ${banner}
      <div class="kof-table">
        <div class="kof-row kof-row--head">
          <div>Feld</div><div>Default (geteilt)</div><div>Override (Artikel)</div>
        </div>
        ${rows.map(renderRow).join('')}
      </div>
    </div>`;

  wireRows(host, ctx, listing);
}

function renderRow(r: OverrideRow): string {
  const kind = valueInputKind(r.type);
  const label = escapeHtml(r.description ?? r.name ?? `Attribut #${r.attribute_id}`);
  const unit = unitHint(r);
  const attr = escapeHtml(r.attribute_id);
  const eff = isPresent(r.override_value) ? r.override_value : r.default_value;
  const effText = formatEffective(kind, eff);
  const sharedBadge = r.is_shared_set ? ` <span class="kof-badge" title="Auswahlwert aus einer Kaufland-Werteliste">Auswahl</span>` : '';
  const ovSet = isPresent(r.override_value);

  return `
    <div class="kof-row" data-of-row="${attr}">
      <div style="min-width:0;">
        <div class="kof-field-label">${label}${unit ? ` <span class="kof-field-unit">(${escapeHtml(unit)})</span>` : ''}${sharedBadge}</div>
        <div class="kof-field-meta">${escapeHtml(r.name ?? '')} · ${escapeHtml(r.type ?? 'text')}</div>
        <div class="kof-field-eff" data-of-eff="${attr}">effektiv: <b>${effText === '' ? '— (leer)' : escapeHtml(effText)}</b></div>
      </div>
      <div class="kof-cell">
        ${renderInput(kind, 'default', attr, r.default_value, unit)}
        <span class="kof-cell-cap">geteilt</span>
      </div>
      <div class="kof-cell">
        ${renderInput(kind, 'override', attr, r.override_value, unit)}
        <span class="kof-cell-cap${ovSet ? ' is-set' : ''}">${ovSet ? '✓ nur dieser Artikel' : 'nur dieser Artikel'}</span>
      </div>
    </div>`;
}

/** Baut ein typ- + rollen-gerechtes Eingabefeld. Leerwert ('' bzw. Bool-Leeroption)
 *  bedeutet "ungesetzt" — beim Speichern wird der Eintrag geloescht (Override erbt dann
 *  den Default, Default wird zu null). Bei gesetztem Override wird das Feld hervorgehoben. */
function renderInput(kind: InputKind, role: 'default' | 'override', attr: string, value: unknown, unit: string): string {
  const isSet = role === 'override' && isPresent(value);
  const setCls = isSet ? ' is-set' : '';
  const common = `data-of-input="${attr}" data-of-role="${role}" data-of-kind="${kind}" class="${setCls.trim()}"`;
  if (kind === 'bool') {
    const cur = value === true ? 'true' : value === false ? 'false' : '';
    const inheritLabel = role === 'override' ? '— erbt Default' : '— (leer)';
    return `
      <select ${common}>
        <option value=""${cur === '' ? ' selected' : ''}>${inheritLabel}</option>
        <option value="true"${cur === 'true' ? ' selected' : ''}>Ja</option>
        <option value="false"${cur === 'false' ? ' selected' : ''}>Nein</option>
      </select>`;
  }
  const placeholder = role === 'override' ? 'erbt Default' : '—';
  const type = kind === 'number' ? 'number' : 'text';
  const input = `<input type="${type}" ${common} value="${escapeHtml(valueToText(value))}" placeholder="${placeholder}" />`;
  if (unit) {
    return `<div class="kof-input-unit">${input}<span class="kof-unit">${escapeHtml(unit)}</span></div>`;
  }
  return input;
}

function wireRows(host: HTMLElement, ctx: OptionalFieldsCtx, listing: OptionalFieldsListing): void {
  host.querySelectorAll<HTMLInputElement | HTMLSelectElement>('[data-of-input]').forEach((el) => {
    el.addEventListener('change', () => void saveValue(host, ctx, listing, el));
  });
  host.querySelector<HTMLButtonElement>('#of-refresh')?.addEventListener('click', () => void refreshFields(host, ctx, listing));
}

/** Stoesst refresh_category_attributes fuer die Listing-Kategorie an und laedt den Tab nach
 *  kurzer Wartezeit neu (Worker-Job laeuft async). */
async function refreshFields(host: HTMLElement, ctx: OptionalFieldsCtx, listing: OptionalFieldsListing): Promise<void> {
  if (!listing.kaufland_category_id) return;
  const btn = host.querySelector<HTMLButtonElement>('#of-refresh');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span> Aktualisiere …'; }
  const { error } = await ctx.supabase.rpc('kaufland_enqueue_refresh_category_attributes', {
    _storefront: listing.storefront,
    _category_id: listing.kaufland_category_id,
    _locale: localeFor(listing.storefront),
  });
  if (error) {
    showToast(`Aktualisierung fehlgeschlagen: ${error.message}`, 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="v-icon fa-light fa-rotate"></span> Aktualisieren'; }
    return;
  }
  showToast('Aktualisierung läuft — Felder werden in wenigen Sekunden neu geladen …', 'info');
  setTimeout(() => { void renderOptionalFieldsTab(host, ctx, listing); }, 4000);
}

/** Liest den Roh-Input und gibt den jsonb-tauglichen Wert zurueck (null = ungesetzt). */
function parseInputValue(el: HTMLInputElement | HTMLSelectElement, kind: string): unknown {
  const raw = el.value;
  if (kind === 'bool') return raw === '' ? null : raw === 'true';
  if (kind === 'number') return raw === '' ? null : Number(raw);
  return raw === '' ? null : raw;
}

async function saveValue(
  host: HTMLElement,
  ctx: OptionalFieldsCtx,
  listing: OptionalFieldsListing,
  el: HTMLInputElement | HTMLSelectElement,
): Promise<void> {
  const attr = el.dataset['ofInput']!;
  const role = el.dataset['ofRole']!;
  const kind = el.dataset['ofKind']!;
  const value = parseInputValue(el, kind);

  const rpc = role === 'override' ? 'kaufland_set_optional_field_override' : 'kaufland_set_optional_field_value';
  const args = role === 'override'
    ? { _storefront: listing.storefront, _attribute_id: attr, _vario_article_id: listing.vario_article_id, _value: value }
    : { _storefront: listing.storefront, _attribute_id: attr, _value: value };

  const { error } = await ctx.supabase.rpc(rpc, args);
  if (error) { showToast(`Speichern fehlgeschlagen: ${error.message}`, 'error'); return; }
  showToast('Gespeichert', 'success');

  // Override-Hervorhebung (orangener Rahmen + Caption) live nachziehen.
  if (role === 'override') {
    const set = value !== null;
    el.classList.toggle('is-set', set);
    const cap = el.closest('.kof-cell')?.querySelector('.kof-cell-cap');
    if (cap) {
      cap.classList.toggle('is-set', set);
      cap.textContent = set ? '✓ nur dieser Artikel' : 'nur dieser Artikel';
    }
  }
  updateEffective(host, attr);
}

/** Aktualisiert den "effektiv:"-Hinweis einer Zeile aus den aktuellen Default-/Override-Inputs. */
function updateEffective(host: HTMLElement, attr: string): void {
  const row = host.querySelector<HTMLElement>(`[data-of-row="${CSS.escape(attr)}"]`);
  if (!row) return;
  const ov = row.querySelector<HTMLInputElement | HTMLSelectElement>('[data-of-role="override"]');
  const df = row.querySelector<HTMLInputElement | HTMLSelectElement>('[data-of-role="default"]');
  const kind = (ov?.dataset['ofKind'] ?? df?.dataset['ofKind'] ?? 'text') as InputKind;
  const effRaw = ov && ov.value !== '' ? ov.value : df ? df.value : '';
  const target = row.querySelector<HTMLElement>(`[data-of-eff="${CSS.escape(attr)}"] b`);
  if (!target) return;
  let text = effRaw;
  if (kind === 'bool') text = effRaw === 'true' ? 'Ja' : effRaw === 'false' ? 'Nein' : '';
  target.textContent = text === '' ? '— (leer)' : text;
}
